import React from 'react';
import { DollarSign, Bitcoin, Wallet } from 'lucide-react';

// Metadatos estáticos de cada método. `implemented` indica si el flujo de pago
// existe de verdad en el código. La disponibilidad efectiva se calcula cruzando
// esto con la config de admin (system/payment_settings.methods).
export interface PaymentMethodCatalogItem {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  implemented: boolean;
}

export interface PaymentMethodView extends PaymentMethodCatalogItem {
  available: boolean;   // se puede elegir ahora
  comingSoon: boolean;  // método aún no implementado (teaser)
}

// Catálogo único de métodos conocidos por la app.
export const PAYMENT_METHOD_CATALOG: PaymentMethodCatalogItem[] = [
  {
    id: 'mercadopago',
    name: 'Mercado Pago',
    description: 'Tarjeta, OXXO o transferencia (SPEI) — activación automática',
    icon: Wallet,
    implemented: true
  },
  {
    id: 'manual',
    name: 'Pago Manual',
    description: 'Transferencia o depósito — sube tu comprobante y activamos en máx. 24 h hábiles',
    icon: DollarSign,
    implemented: true
  },
  {
    id: 'btcpay',
    name: 'Cripto (BTCPay)',
    description: 'Pago con Bitcoin, Litecoin o Lightning — activación automática',
    icon: Bitcoin,
    implemented: true
  }
];

/**
 * Calcula la lista efectiva de métodos a partir del catálogo y la config de
 * admin. Un método implementado está disponible salvo que el admin lo apague
 * explícitamente (config[id] === false). Los no implementados salen como
 * "Próximamente".
 */
export function computePaymentMethods(
  config?: Record<string, boolean>
): PaymentMethodView[] {
  return PAYMENT_METHOD_CATALOG.map((m) => ({
    ...m,
    available: m.implemented && config?.[m.id] !== false,
    comingSoon: !m.implemented
  }));
}

// Lista por defecto (sin config). Se mantiene como export para compatibilidad.
export const PAYMENT_METHODS: PaymentMethodView[] = computePaymentMethods();

interface PaymentMethodsSelectorProps {
  onSelectMethod: (methodId: string) => void;
  selectedMethod: string | null;
  methods?: PaymentMethodView[];
}

const PaymentMethodsSelector: React.FC<PaymentMethodsSelectorProps> = ({
  onSelectMethod,
  selectedMethod,
  methods
}) => {
  // Solo se muestran los disponibles (elegibles) o los "próximamente" (teaser).
  // Un método implementado pero apagado por el admin queda oculto.
  const list = (methods ?? PAYMENT_METHODS).filter(
    (m) => m.available || m.comingSoon
  );

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-medium text-content">
        Selecciona un método de pago
      </h2>

      <div className="grid grid-cols-1 gap-3">
        {list.map((method) => (
          <div
            key={method.id}
            onClick={() => method.available && onSelectMethod(method.id)}
            className={`
              relative p-3 sm:p-4 border rounded-lg transition-all duration-200
              ${selectedMethod === method.id ? 'border-2' : 'border'}
              ${
                method.available
                  ? selectedMethod === method.id
                    ? `border-rgb(var(--c-accent)) bg-rgb(var(--c-accent))10 cursor-pointer`
                    : 'border-line hover:border-line cursor-pointer'
                  : 'border-line opacity-60 cursor-not-allowed'
              }
            `}
            style={{
              borderColor: selectedMethod === method.id ? 'rgb(var(--c-accent))' : undefined,
              backgroundColor: selectedMethod === method.id ? `rgb(var(--c-accent) / 0.12)` : undefined
            }}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0 mr-3">
                <div className="h-9 w-9 flex items-center justify-center rounded-full bg-canvas">
                  <method.icon className={`h-5 w-5 ${selectedMethod === method.id ? 'text-accent' : 'text-content-2'}`} />
                </div>
              </div>

              <div className="flex-grow min-w-0">
                <div className="flex flex-wrap justify-between items-center">
                  <h3 className="text-sm font-medium text-content mr-2">{method.name}</h3>

                  {method.comingSoon && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300 mt-1 sm:mt-0">
                      Próximamente
                    </span>
                  )}
                </div>

                <p className="mt-1 text-xs sm:text-sm text-content-3 truncate">
                  {method.description}
                </p>
              </div>

              {/* Radio indicator - only show if available */}
              {method.available && (
                <div className="ml-3 flex-shrink-0">
                  <div className={`h-5 w-5 rounded-full flex items-center justify-center border ${
                    selectedMethod === method.id
                      ? 'border-blue-600'
                      : 'border-line'
                  }`}>
                    {selectedMethod === method.id && (
                      <div className="h-3 w-3 rounded-full bg-blue-600" />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentMethodsSelector;
